import { apiClient } from './apiClient';
import { logError } from '../utils/errorLogger';

export interface AdminDashboardStats {
  users: {
    total: number;
    active: number;
    admins: number;
    hosts: number;
    guests: number;
    growth_rate: number;
  };
  events: {
    total: number;
    active: number;
    pending: number;
    growth_rate: number;
  };
  guests: {
    total: number;
    accepted: number;
    pending_responses: number;
    response_rate: number;
  };
  recent_activity: {
    new_events_week: number;
    new_responses_week: number;
  };
}

export interface HostDashboardStats {
  events: {
    total: number;
    active: number;
    growth_rate: number;
  };
  guests: {
    total: number;
    accepted: number;
    pending: number;
    rejected: number;
    response_rate: number;
    growth_rate: number;
  };
  recent_activity: {
    new_responses_week: number;
    new_events_week: number;
  };
  upcoming_events: Array<{
    id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    total_guests: number;
    responses: number;
    response_rate: number;
    status: string;
    sub_events?: Array<{
      id: string;
      title: string;
      date: string;
      time: string;
      location: string;
      total_guests: number;
      responses: number;
      response_rate: number;
      status: string;
    }>;
  }>;
}

export interface GuestDashboardStats {
  invitations: {
    total: number;
    accepted: number;
    pending: number;
    rejected: number;
  };
  recent_activity: {
    responses_week: number;
  };
  upcoming_events: Array<{
    id: string;
    guest_event_id: number;
    invitation_token?: string;
    title: string;
    host: string;
    date: string;
    time: string;
    location: string;
    description: string;
    status: string;
    companion_name: string;
    companion_count: number;
    is_past: boolean;
    image_url?: string | null;
    image_shape?: 'circle' | 'square' | 'heart' | 'star' | null;
    companions?: Array<{
      guest_event_id: number;
      name: string;
      status: string;
    }>;
  }>;
}

export interface ActivityItem {
  id: string;
  type: string;
  message: string;
  time: string;
  user: string;
}

export interface RecentActivity {
  activities: ActivityItem[];
}

export interface PendingApproval {
  id: string;
  type: string;
  title: string;
  host_name: string;
  host_email: string;
  event_date: string | null;
  location: string;
  guest_count: number;
  response_count: number;
  created_at: string;
  submitted_by_role: string;
  requires_approval: boolean;
  description: string;
}

export interface PendingApprovals {
  approvals: PendingApproval[];
  total_count: number;
}

class DashboardService {
  /**
   * Get admin dashboard statistics
   */
  async getAdminStats(): Promise<AdminDashboardStats> {
    try {
      const response = await apiClient.get('/dashboard/admin/stats/');
      return response.data.data;
    } catch (error) {
      logError('Error fetching admin dashboard stats', error);
      throw error;
    }
  }

  /**
   * Get host dashboard statistics
   */
  async getHostStats(): Promise<HostDashboardStats> {
    try {
      const response = await apiClient.get('/dashboard/host/stats/');
      return response.data.data;
    } catch (error) {
      logError('Error fetching host dashboard stats', error);
      throw error;
    }
  }

  /**
   * Get guest dashboard statistics
   */
  async getGuestStats(): Promise<GuestDashboardStats> {
    try {
      const response = await apiClient.get('/dashboard/guest/stats/', { cache: false });
      return response.data.data;
    } catch (error) {
      logError('Error fetching guest dashboard stats', error);
      throw error;
    }
  }

  /**
   * Get recent activity for all user types
   */
  async getRecentActivity(): Promise<RecentActivity> {
    try {
      const response = await apiClient.get('/dashboard/activity/');
      return response.data.data;
    } catch (error) {
      logError('Error fetching recent activity', error);
      throw error;
    }
  }

  /**
   * Get pending approvals for admin
   */
  async getPendingApprovals(): Promise<PendingApprovals> {
    try {
      const response = await apiClient.get('/dashboard/pending-approvals/', { cache: false });
      return response.data.data;
    } catch (error) {
      logError('Error fetching pending approvals', error);
      throw error;
    }
  }

  /**
   * Get dashboard stats based on user role
   */
  async getDashboardStats(userRole: 'admin' | 'host' | 'guest' | 'usher'): Promise<AdminDashboardStats | HostDashboardStats | GuestDashboardStats> {
    switch (userRole) {
      case 'admin':
        return this.getAdminStats();
      case 'host':
        return this.getHostStats();
      case 'guest':
        return this.getGuestStats();
      case 'usher':
        return this.getGuestStats(); // Ushers use guest dashboard for now
      default:
        throw new Error(`Invalid user role: ${userRole}`);
    }
  }
}

export const dashboardService = new DashboardService();
